import type React from 'react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { organizationSlice } from 'Flux/Slice/Organization/OrganizationSlice.ts'
import { useGetOrganizationByIdSelector } from 'Flux/Selector/Organization/GetOrganizations/GetOrganizationsSelector.ts'
import { useGetOrganizationMembersSelector } from 'Flux/Selector/Organization/GetOrganizationMembers/GetOrganizationMembersSelector.ts'
import { useGetAuthenticatedUser } from 'Flux/Selector/index.ts'
import type { IUser } from 'Model/index.ts'
import { OrganizationType } from 'Model/Organization/OrganizationType.ts'
import type { SampleConfigurationTypeKey } from 'Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from 'Generated/SampleConfigurationType.js'
import {
  CheckIcon,
  CreditCardIcon,
  InformationCircleIcon,
  PencilIcon,
  TrashIcon,
  TrophyIcon,
  UsersIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import type { RedlistModeKey } from 'Generated/RedlistMode.js'
import { RedlistModes } from 'Generated/RedlistMode.js'
import type { ProcessingTypeKey } from 'Generated/ProcessingType.ts'
import { ProcessingTypes } from 'Generated/ProcessingType.ts'
import { OrganizationWidget } from '../../Component/Organization/OrganizationWidget.tsx'
import Actions from '../../../Flux/index.ts'
import { OrganizationLogo } from '../../Component/Organization/OrganizationLogo.tsx'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage, successMessage } from '../../../Model/Message/IMessage.js'
import { tracingAxios } from '../../../Repository/Sentry.js'
import { Environment } from '../../../environment.js'
import { trackOrgAction } from '../../../Services/Telemetry/index.js'
import { useModal } from '../../Modal/UseModal.js'
import { parseTier } from 'Util/billing.js'

const STATUS_STYLES: Record<string, string> = {
  active: 'badge-success',
  non_renewing: 'badge-warning'
}

export const OrganizationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const organization = useGetOrganizationByIdSelector(id || '')
  const members = useGetOrganizationMembersSelector(id || '')
  const currentUser = useGetAuthenticatedUser()

  const [isEditing, setIsEditing] = useState(false)
  const [isPublic, setIsPublic] = useState<boolean>(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<OrganizationType>(OrganizationType.LABORATORY)
  const [homepage, setHomepage] = useState<string>('')
  const [country, setCountry] = useState<string>('')
  const [specialities, setSpecialities] = useState<string[]>([])
  const [specialitiesInput, setSpecialitiesInput] = useState<string>('')
  const [contactEmail, setContactEmail] = useState<string>('')
  const [defaultSampleConfigurationType, setDefaultSampleConfigurationType] = useState<SampleConfigurationTypeKey>('SINGLE_SAMPLE_PAIRS')
  const [defaultRedlistMode, setDefaultRedlistMode] = useState<RedlistModeKey>('NONE')
  const [defaultProcessingType, setDefaultProcessingType] = useState<ProcessingTypeKey>('METABARCODING')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteFirstName, setInviteFirstName] = useState('')
  const [inviteLastName, setInviteLastName] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)
  const { openModal } = useModal()

  // Check if user is from the same organization
  const isUserFromSameOrg = currentUser?.org?.id === id || false

  // Check if user is an admin of the organization
  const isAdmin = (isUserFromSameOrg && currentUser?.roles?.includes('org-admin')) || false

  useEffect(() => {
    if (id) {
      // Fetch organization data
      dispatch(organizationSlice.actions.getOrganizationRequest({ organizationId: id }))

      // If user is from the same organization, fetch members
      if (isUserFromSameOrg) {
        dispatch(organizationSlice.actions.getOrganizationMembersRequest({ organizationId: id }))
      }
    }
  }, [dispatch, id, isUserFromSameOrg])

  useEffect(() => {
    if (organization) {
      setName(organization.name)
      setDescription(organization.description || '')
      setType(organization.type as OrganizationType)
      setIsPublic(organization.isPublic)
      setHomepage(organization.homepage || '')
      setCountry(organization.country || '')
      setSpecialities(organization.specialities || [])
      setContactEmail(organization.contactEmail || '')
      setDefaultSampleConfigurationType(organization.defaultSampleConfigurationType)
      setDefaultRedlistMode(organization.defaultRedlistMode)
      setDefaultProcessingType(organization.defaultProcessingType)
    }
  }, [organization])

  const handleSave = () => {
    if (id && isAdmin) {
      dispatch(
        organizationSlice.actions.updateOrganizationRequest({
          organizationId: id,
          name,
          description,
          type,
          isPublic,
          homepage,
          country,
          specialities,
          contactEmail,
          defaultSampleConfigurationType,
          defaultRedlistMode,
          defaultProcessingType
        })
      )
      setIsEditing(false)
      dispatch(addMessage(successMessage('Changes applied successfully. Members may need to re-login for all information to be applied')))
    }
  }

  const handleAddSpeciality = () => {
    if (specialitiesInput.trim()) {
      setSpecialities([...specialities, specialitiesInput.trim()])
      setSpecialitiesInput('')
    }
  }

  const handleRemoveSpeciality = (index: number) => {
    const newSpecialities = [...specialities]
    newSpecialities.splice(index, 1)
    setSpecialities(newSpecialities)
  }

  if (!organization) {
    return <div className="text-xl">Loading organization...</div>
  }

  // For users from other organizations, only show name, description, and type if public
  if (!isUserFromSameOrg) {
    return <OrganizationWidget organization={organization} />
  }

  return (
    <div className="bg-slate-100 rounded-lg p-4 drop-shadow-sm mx-auto max-w-[100rem]">
      <div className="flex items-stretch text-sm">
        <div className="flex-1 flex-col flex">
          <span className="text-neutral-400 align-baseline">Organization#{organization.id}</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {isAdmin && !isEditing && (
            <button data-testid="edit-profile" className="btn btn-sm btn-outline" onClick={() => setIsEditing(true)}>
              <PencilIcon className="w-4 h-4 mr-1" />
              Edit
            </button>
          )}
        </div>
      </div>

      <h1 className="text-2xl mb-4 pl-4 flex items-center">
        <div className="relative">{organization.name}</div>
      </h1>
      <hr className="py-2" />

      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-slate-50 p-4 rounded-lg">
          <div className="space-y-2">
            {isEditing && isAdmin ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    data-testid={'org-description-edit'}
                    className="textarea textarea-bordered w-full"
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select
                    className="select select-bordered w-full"
                    value={type}
                    onChange={(e) => setType(e.target.value as OrganizationType)}
                  >
                    <option value={OrganizationType.CUSTOMER}>Customer</option>
                    <option value={OrganizationType.LABORATORY}>Laboratory</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Homepage</label>
                  <input
                    type="url"
                    className="input input-bordered w-full"
                    placeholder="https://example.com"
                    value={homepage}
                    onChange={(e) => setHomepage(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="Country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Specialities</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      className="input input-bordered grow"
                      placeholder="Add a speciality"
                      value={specialitiesInput}
                      onChange={(e) => setSpecialitiesInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSpeciality()}
                    />
                    <button className="btn btn-primary" onClick={handleAddSpeciality} type="button">
                      Add
                    </button>
                  </div>
                  {specialities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {specialities.map((speciality, index) => (
                        <div key={index} className="badge badge-outline gap-1">
                          {speciality}
                          <button className="btn btn-xs btn-circle btn-ghost" onClick={() => handleRemoveSpeciality(index)}>
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {organization.logo && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Logo</label>
                    <OrganizationLogo organization={organization} />
                    <button
                      onClick={() => dispatch(Actions[organizationSlice.name].deleteLogoRequest(organization))}
                      className="btn btn-xs btn-error"
                    >
                      <TrashIcon className={'w-4 h-4'} />
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Upload Logo</label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="file-input file-input-bordered w-full"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return

                      dispatch(Actions[organizationSlice.name].updateLogoRequest({ id: organization.id, logo: file }))
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    className="input input-bordered w-full"
                    placeholder="contact@example.com"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                  />
                </div>

                <div>
                  <TooltipTrigger tooltip={'Spcfy needs to know what kind of samples you are usually processing.'} classNames={'w-full'}>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Default Sample Configuration Type</label>
                    <div className={'flex gap-2'}>
                      <select
                        data-testid={'select-sample-type'}
                        className="select select-bordered w-full"
                        value={defaultSampleConfigurationType}
                        onChange={(e) => setDefaultSampleConfigurationType(e.target.value as SampleConfigurationTypeKey)}
                      >
                        {Object.entries(SampleConfigurationTypes).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value.label}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className={'btn btn-outline btn-xs'}
                        onClick={() =>
                          openModal(
                            'sampleConfigurationType',
                            {
                              initial: defaultSampleConfigurationType
                            },
                            (result) => setDefaultSampleConfigurationType(result)
                          )
                        }
                      >
                        Help me choose
                      </button>
                    </div>
                  </TooltipTrigger>
                </div>

                <div>
                  <TooltipTrigger tooltip={'Default redlist mode for new orders. This can be overridden per order.'} classNames={'w-full'}>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Default Redlist Mode</label>
                    <select
                      className="select select-bordered w-full"
                      value={defaultRedlistMode}
                      onChange={(e) => setDefaultRedlistMode(e.target.value as RedlistModeKey)}
                    >
                      {Object.entries(RedlistModes).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value.label}
                        </option>
                      ))}
                    </select>
                  </TooltipTrigger>
                </div>

                <div>
                  <TooltipTrigger
                    tooltip={'Default processing type for new orders. This can be overridden per order.'}
                    classNames={'w-full'}
                  >
                    <label className="block text-sm font-medium text-slate-700 mb-1">Default Processing Type</label>
                    <select
                      className="select select-bordered w-full"
                      value={defaultProcessingType}
                      onChange={(e) => setDefaultProcessingType(e.target.value as ProcessingTypeKey)}
                    >
                      {Object.entries(ProcessingTypes).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value.label}
                        </option>
                      ))}
                    </select>
                  </TooltipTrigger>
                </div>

                <div className="mt-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="toggle toggle-primary mr-2"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                    />
                    <span className="text-sm font-medium text-slate-700">Public Organization</span>
                  </label>
                  <p className="text-xs text-slate-500 mt-1 ml-10">
                    Public organizations are visible to all users. Their name, description, and type can be seen by anyone.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <button data-testid="save-btn" className="btn btn-primary" onClick={handleSave}>
                    <CheckIcon className="w-4 h-4 mr-1" />
                    Save
                  </button>
                  <button className="btn btn-outline" onClick={() => setIsEditing(false)}>
                    <XMarkIcon className="w-4 h-4 mr-1" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <OrganizationWidget organization={organization} flat hideTitle />
              </>
            )}
          </div>
        </div>

        <div className="bg-slate-50 p-4 rounded-lg w-full">
          <h2 className="text-lg font-semibold mb-2"></h2>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <UsersIcon className="w-6 h-6 text-blue-600" />
              <div>
                <p className="text-lg font-medium">{members?.length || 0}</p>
                <p className="text-sm text-slate-500">Members</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <InformationCircleIcon className="w-6 h-6 text-green-600" />
              <div>
                <p className="text-lg font-medium">{organization.type}</p>
                <p className="text-sm text-slate-500">Organization Type</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {organization.isPublic ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                )}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <div>
                <p className="text-lg font-medium">{organization.isPublic ? 'Public' : 'Private'}</p>
                <p className="text-sm text-slate-500">Visibility Status</p>
                {organization.isPublic && <p className="text-xs text-slate-500 mt-1">This organization is visible to all users</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TrophyIcon className="w-7 h-7" />
              <div>
                <p className="text-slate-500 mt-2">
                  {currentUser?.org?.chargebeeEntitlements && currentUser?.org.chargebeeEntitlements.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-1 max-w-xl">
                      {currentUser.org.chargebeeEntitlements.map((e) => (
                        <span key={e} className="badge badge-ghost uppercase opacity-70">
                          {e.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </p>
                <p className="text-sm text-slate-500">Feature Access</p>
              </div>
            </div>
            {isUserFromSameOrg && currentUser?.org?.chargebeePlanId && (
              <div className="flex items-center gap-2">
                <CreditCardIcon className="w-6 h-6 text-teal-600" />
                <div>
                  <p className="text-lg font-medium capitalize">{parseTier(currentUser.org.chargebeePlanId)}</p>
                  <p className="text-sm text-slate-500">Subscription Plan</p>
                  <div className="flex gap-2 mt-1">
                    {currentUser.org.chargebeeSubscriptionStatus && (
                      <span className={`badge badge-sm ${STATUS_STYLES[currentUser.org.chargebeeSubscriptionStatus] ?? 'badge-error'}`}>
                        {currentUser.org.chargebeeSubscriptionStatus}
                      </span>
                    )}
                    {currentUser.org.commercialType && (
                      <span className="badge badge-sm badge-outline">{currentUser.org.commercialType}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <TooltipTrigger
                tooltip={'The kind of samples/fastq files this lab is usually processing. Provides defaults for spcfy settings.'}
                classNames={'w-full'}
              >
                <p className="text-lg font-medium" data-testid={'selected-sample-config'}>
                  {SampleConfigurationTypes[organization.defaultSampleConfigurationType].label}
                </p>
                <p className="text-sm text-slate-500">Default Sample Configuration</p>
              </TooltipTrigger>
            </div>
            <div className="flex items-center gap-2">
              <TooltipTrigger tooltip={'Default redlist mode for new orders. This can be overridden per order.'} classNames={'w-full'}>
                <p className="text-lg font-medium">{RedlistModes[organization.defaultRedlistMode].label}</p>
                <p className="text-sm text-slate-500">Default Redlist Mode</p>
              </TooltipTrigger>
            </div>
            <div className="flex items-center gap-2">
              <TooltipTrigger tooltip={'Default processing type for new orders. This can be overridden per order.'} classNames={'w-full'}>
                <p className="text-lg font-medium">{ProcessingTypes[organization.defaultProcessingType].label}</p>
                <p className="text-sm text-slate-500">Default Processing Type</p>
              </TooltipTrigger>
            </div>
          </div>
        </div>
      </div>

      {isUserFromSameOrg && (
        <>
          {members && members.length > 0 && !members.some((member) => member.isContact) && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex">
                <div className="shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    No user is marked as organization contact. This means you won&apos;t be able to receive e-mails for your organization
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="bg-slate-50 p-4 rounded-lg mb-6">
            {/* Admin Members Section */}
            {members?.some((member) => member.roles?.includes('ORG_ADMIN')) && (
              <>
                <h3 className="text-md font-medium mb-2 text-slate-700">Administrators</h3>
                <div className="divide-y mb-6 overflow-y-auto">
                  {members
                    ?.filter((member) => member.roles?.includes('ORG_ADMIN'))
                    .map((member: IUser, index) => (
                      <MemberWidget key={index} member={member} organizationId={id} userIsAdmin={isAdmin} />
                    ))}
                </div>
              </>
            )}

            {/* Regular Members Section */}
            {members?.some((member) => !member.roles?.includes('ORG_ADMIN')) && (
              <>
                <h3 className="text-md font-medium mb-2 text-slate-700">Members</h3>
                <div className="divide-y">
                  {members
                    ?.filter((member) => !member.roles?.includes('ORG_ADMIN'))
                    .map((member: IUser, index) => (
                      <MemberWidget key={index} member={member} organizationId={id} userIsAdmin={isAdmin} />
                    ))}
                </div>
              </>
            )}
            {/* invite section */}
            {isAdmin && (
              <div className="bg-slate-50 p-4 rounded-lg mb-6">
                <h3 className="text-md font-medium mb-2 text-slate-700">Invite New Member</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="First Name"
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                  />
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="Last Name"
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                  />
                  <input
                    type="email"
                    className="input input-bordered w-full"
                    placeholder="Email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-2 sm:w-full">
                  <button
                    className="btn btn-primary sm:w-full"
                    disabled={inviteLoading || !inviteEmail || !inviteFirstName || !inviteLastName}
                    onClick={async () => {
                      try {
                        setInviteLoading(true)
                        await tracingAxios.post(
                          `${Environment.API_URL}/api/registration/invite`,
                          {
                            email: inviteEmail,
                            firstname: inviteFirstName,
                            lastname: inviteLastName
                          },
                          { withCredentials: true }
                        )
                        trackOrgAction('member_invited')
                        dispatch(addMessage(successMessage(`Invitation sent to ${inviteEmail}`)))
                        setInviteEmail('')
                        setInviteFirstName('')
                        setInviteLastName('')
                      } catch (err) {
                        const msg =
                          err &&
                          typeof err === 'object' &&
                          'response' in err &&
                          err.response &&
                          typeof err.response === 'object' &&
                          'data' in err.response &&
                          err.response.data !== null &&
                          err.response.data !== undefined
                            ? err.response.data.toString()
                            : undefined
                        const errorMsg = msg || 'Failed to send invitation. Please try again.'
                        dispatch(addMessage(errorMessage(errorMsg)))
                      } finally {
                        setInviteLoading(false)
                      }
                    }}
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

interface MemberWidgetProps {
  member: IUser
  userIsAdmin: boolean
  organizationId: string | undefined
}

export const MemberWidget = ({ member, userIsAdmin, organizationId }: MemberWidgetProps) => {
  const dispatch = useDispatch()
  const handleUpdateMember = (memberId: string, setAdmin: boolean, isContact: boolean) => {
    if (organizationId && userIsAdmin) {
      dispatch(
        organizationSlice.actions.updateOrganizationMemberRequest({
          organizationId: organizationId,
          memberId,
          isAdmin: setAdmin,
          isContact
        })
      )
    }
  }
  return (
    <div key={member.id} className="py-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <p className="font-medium">{`${member.firstName} ${member.lastName}`}</p>
          <p className="text-sm text-slate-500">{member.email}</p>
        </div>
        {userIsAdmin && (
          <div className="flex flex-wrap gap-2 mt-2">
            {!member.roles?.includes('ORG_ADMIN') && (
              <span
                className={`btn btn-xs btn-outline cursor-pointer`}
                onClick={() => handleUpdateMember(member.id, true, member.isContact || false)}
              >
                Make Admin
              </span>
            )}
            {member.roles?.includes('ORG_ADMIN') && (
              <span
                className={`btn btn-xs btn-outline cursor-pointer btn-primary`}
                onClick={() => handleUpdateMember(member.id, false, member.isContact || false)}
              >
                Remove Admin
              </span>
            )}
            {member.isContact && (
              <span
                className={`btn btn-xs btn-outline btn-primary cursor-pointer`}
                onClick={() => handleUpdateMember(member.id, member.roles?.includes('ORG_ADMIN') || false, false)}
              >
                Remove Contact
              </span>
            )}
            {!member.isContact && (
              <button
                className="btn btn-xs btn-outline whitespace-nowrap"
                onClick={() => handleUpdateMember(member.id, member.roles?.includes('ORG_ADMIN') || false, true)}
              >
                Make Contact
              </button>
            )}
          </div>
        )}
        {!userIsAdmin && (
          <div className="flex flex-wrap gap-2 mt-2">
            {member.roles?.includes('ORG_ADMIN') && <span className={`badge badge-xs badge-outline badge-primary py-2`}>Admin</span>}
            {member.isContact && <span className={`badge badge-xs badge-outline badge-primary py-2`}>Contact</span>}
          </div>
        )}
      </div>
    </div>
  )
}
